import {count, MetricType} from 'metrics/Metrics';

export type ActivationSource = 'config_flag' | 'url_param' | 'none';

const snapshotExportMetrics = {
  chipHits: new Map<string, number>(),
  chipMisses: new Map<string, number>(),
  chipTotalLookups: 0,
};

export function resetSnapshotExportMetrics(): void {
  snapshotExportMetrics.chipHits.clear();
  snapshotExportMetrics.chipMisses.clear();
  snapshotExportMetrics.chipTotalLookups = 0;
}

export function incrementSnapshotHit(chipType: string): void {
  snapshotExportMetrics.chipTotalLookups++;
  snapshotExportMetrics.chipHits.set(chipType, (snapshotExportMetrics.chipHits.get(chipType) ?? 0) + 1);
}

export function incrementSnapshotMiss(chipType: string, reason: string): void {
  snapshotExportMetrics.chipTotalLookups++;
  const key = `${chipType}:${reason}`;
  snapshotExportMetrics.chipMisses.set(key, (snapshotExportMetrics.chipMisses.get(key) ?? 0) + 1);
}

/** Emit accumulated chip metrics to Datadog and reset. Called on SnapshotContextProvider unmount. */
export function flushSnapshotExportMetrics(flagEnabled: boolean, activationSource: ActivationSource): void {
  const m = snapshotExportMetrics;
  if (m.chipTotalLookups === 0) {
    return;
  }

  const tags = (chipType: string): {chip_type: string; flag_enabled: boolean; activation_source: string} => ({
    chip_type: chipType,
    flag_enabled: flagEnabled,
    activation_source: activationSource,
  });

  for (const [chipType, v] of m.chipHits) {
    count('snapshot_export.chip_hits', v, tags(chipType));
  }

  for (const [key, v] of m.chipMisses) {
    const [chipType, reason] = key.split(':');
    count('snapshot_export.chip_misses', v, {...tags(chipType), reason});
  }

  if (m.chipMisses.size === 0) {
    count('snapshot_export.chip_misses', 0, {
      chip_type: 'all',
      reason: 'none',
      flag_enabled: flagEnabled,
      activation_source: activationSource,
    });
  }

  resetSnapshotExportMetrics();
}

export interface BeaconMetric {
  metricType: MetricType;
  metricName: string;
  tags: Record<string, string | number | boolean>;
  value: number;
}

export function buildSnapshotMetricsPayload(
  flagEnabled: boolean,
  activationSource: ActivationSource
): Array<BeaconMetric> | null {
  const m = snapshotExportMetrics;
  if (m.chipTotalLookups === 0) {
    return null;
  }

  const metricData: Array<BeaconMetric> = [];

  const tags = (chipType: string): {chip_type: string; flag_enabled: boolean; activation_source: string} => ({
    chip_type: chipType,
    flag_enabled: flagEnabled,
    activation_source: activationSource,
  });

  for (const [chipType, v] of m.chipHits) {
    metricData.push({
      metricType: MetricType.COUNT,
      metricName: 'snapshot_export.chip_hits',
      tags: tags(chipType),
      value: v,
    });
  }

  for (const [key, v] of m.chipMisses) {
    const [chipType, reason] = key.split(':');
    metricData.push({
      metricType: MetricType.COUNT,
      metricName: 'snapshot_export.chip_misses',
      tags: {...tags(chipType), reason},
      value: v,
    });
  }

  if (m.chipMisses.size === 0) {
    metricData.push({
      metricType: MetricType.COUNT,
      metricName: 'snapshot_export.chip_misses',
      tags: {
        chip_type: 'all',
        reason: 'none',
        flag_enabled: flagEnabled,
        activation_source: activationSource,
      },
      value: 0,
    });
  }

  return metricData;
}

/**
 * Flush accumulated snapshot metrics via sendBeacon for guaranteed delivery before page teardown.
 * Uses the same payload shape as MetricsQueue.send() (POST /1/api/metrics).
 */
export function sendBeaconSnapshotMetrics(flagEnabled: boolean, activationSource: ActivationSource): void {
  const metricData = buildSnapshotMetricsPayload(flagEnabled, activationSource);
  if (metricData == null) {
    return;
  }

  try {
    const payload = JSON.stringify({metricData});
    navigator.sendBeacon('/1/api/metrics', new Blob([payload], {type: 'application/json'}));
    // eslint-disable-next-line benchlint/always-throw-from-catch -- metrics are best-effort; must never break export
  } catch {
    // sendBeacon unavailable or blocked — silently drop metrics
  }

  resetSnapshotExportMetrics();
}
